<script>
import Contact from '@phoenix/mixins/components/light-checkout/contact/Contact';
import formBasicMixin from '@phoenix/mixins/formBasicMixin';
import panelMixin from '@phoenix/mixins/panelMixin';
import CheckboxField from '@maison/components/form/fields/CheckboxField.vue';
import CheckboxListField from '@maison/components/form/fields/CheckboxListField.vue';
import Panel from '@maison/components/common/Panel.vue';
import PhoenixForm from '@maison/components/form/PhoenixForm.vue';
import PictureLazy from '@maison/components/common/PictureLazy.vue';
import SelectField from '@maison/components/form/fields/SelectField.vue';
import TextareaField from '@maison/components/form/fields/TextareaField.vue';
import formAddressFieldsMixin from '@phoenix/mixins/formAddressFieldsMixin';
import { set } from 'lodash-es';

export default {
  components: {
    CheckboxField,
    CheckboxListField,
    Panel,
    PhoenixForm,
    PictureLazy,
    SelectField,
    TextareaField,
  },

  mixins: [formBasicMixin, formAddressFieldsMixin, panelMixin, Contact],

  props: {
    reference: {
      type: Object,
      required: true,
    },
    blockId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      formConfigName: 'lightCheckout',
      modelReferenceFromPanel: null,
      panelOptions: {
        onShow: (event) => {
          this.modelReferenceFromPanel = event.dataset.reference;
        },
      },
    };
  },

  computed: {
    formAction() {
      return window.route('api.lightecom.contact.checkout');
    },
    modelReference() {
      return this.modelReferenceFromPanel ?? this.reference.modelReference;
    },
  },

  watch: {
    modelReferenceFromPanel() {
      if (this.modelReferenceFromPanel) {
        set(this.fields, ['reference', 'value'], this.modelReferenceFromPanel);
      }
    },
    'fields.phone'() {
      set(this.fields, ['phone', 'required'], this.fieldIsPresent('phone'));
    },
  },

  created() {
    this.setFields({
      country: {
        id: 'country',
        name: 'country',
        label: this.$t('Form.label.country'),
        value: null,
        options: [],
        required: true,
        type: 'select',
        forceSelect: true,
      },
      preferredContact: {
        id: 'preferredContact',
        name: 'preferredContact',
        label: this.$t('Form.label.preferedContactMode'),
        value: [],
        required: true,
        options: [
          {
            id: 'contact-mode-email',
            value: 'email',
            label: this.$t('LightCheckout.contactMode.email'),
          },
          {
            id: 'contact-mode-phone',
            value: 'phone',
            label: this.$t('LightCheckout.contactMode.phone'),
          },
          {
            id: 'contact-mode-messaging',
            value: 'messaging',
            label: this.$t('LightCheckout.contactMode.messaging'),
          },
        ],
      },
      additionalInfo: {
        id: 'additionalInfo',
        name: 'additionalInfo',
        label: this.$t('LightCheckout.additionalInformationsTitle'),
        value: '',
        required: false,
      },
      block: {
        id: 'blockId',
        value: this.blockId,
        type: 'hidden',
      },
      reference: {
        id: 'reference',
        value: this.modelReference,
        type: 'hidden',
      },
      formName: {
        id: 'formName',
        value: this.formConfigName,
        type: 'hidden',
      },
      newsletter: {
        id: 'newsletter',
        name: 'newsletter',
        label: this.$t('Form.stayInTouch', {
          privacyLink: this.privacyLink,
        }),
        type: 'checkbox',
        value: false,
      },
    });
  },

  methods: {
    pushFormSubmittedEvent() {
      window.tracking.pushEvent({
        event: 'order_online_form_submitted',
        reference: this.modelReference,
        country: this.fields.country.value,
        website_version: window.Phoenix.config.RELEASE,
      });
    },

    onFormSubmitSuccess() {
      this.$refs.lightCheckoutPanel?.close?.();
      this.pushFormSubmittedEvent();
    },
  },
};
</script>

<template>
  <panel
    :id="id"
    ref="lightCheckoutPanel"
    data-cy="light-checkout-panel"
    class="sgphx-theme-light"
    :options="panelOptions"
    :title="$t('LightCheckout.cartTitle')"
    reverse
  >
    <template #content>
      <div class="sgphx-list sgphx-list--stacked sgphx-list--lg">
        <div class="sgphx-text-muted">
          {{ $t('LightCheckout.cartSubtitle') }}
        </div>

        <div class="sgphx-product-card-key">
          <div class="sgphx-product-card-key__media">
            <div class="sgphx-picture">
              <picture-lazy v-bind="reference.image" />
            </div>
          </div>

          <div class="sgphx-product-card-key__body">
            <a :href="reference.url" class="sgphx-h5 sgphx-expand-target sgphx-h6 sgphx-leading-xs sgphx-mb-xs">
              {{ reference.fullProductTitle }}
            </a>

            <div class="sgphx-text-xs sgphx-text-muted">{{ modelReference }}</div>

            <div class="sgphx-text-xs sgphx-text-muted">{{ reference.ppSubtitle ?? reference.description }}</div>
          </div>
        </div>

        <h2 class="sgphx-h5">{{ $t('LightCheckout.contactStep') }}</h2>

        <phoenix-form
          ref="phoenixForm"
          v-model="fields"
          form-type="contact"
          korean-form-type="contact"
          :action="formAction"
          :captcha-enabled="true"
          :custom-submit-text="$t('LightCheckout.submitOrder')"
          :fill-customer-info="false"
          :is-contained="false"
          :on-success-callback="onFormSubmitSuccess"
          :should-reset-form-on-submit="true"
          :show-errors-in-toast="true"
          :show-success-in-toast="true"
          :terms-link="termsLink"
          custom-outer-class="sgphx-mt-md"
        >
          <template #default="{ shouldDisplayNewsletter }">
            <ul class="sgphx-list sgphx-list--stacked sgphx-list--md">
              <li v-if="fieldIsPresent('title')">
                <form-group :attrs="fields.title" is-sgphx>
                  <select-field v-model="fields.title.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('gender')">
                <form-group :attrs="fields.gender" is-sgphx>
                  <select-field v-model="fields.gender.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('firstName2') && isArabicLanguage">
                <form-group :attrs="fields.firstName2" is-sgphx>
                  <text-field v-model="fields.firstName2.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('lastName2') && isArabicLanguage">
                <form-group :attrs="fields.lastName2" is-sgphx>
                  <text-field v-model="fields.lastName2.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('firstName1')">
                <form-group :attrs="fields.firstName1" is-sgphx>
                  <text-field v-model="fields.firstName1.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('lastName1')">
                <form-group :attrs="fields.lastName1" is-sgphx>
                  <text-field v-model="fields.lastName1.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('firstName2') && !isArabicLanguage">
                <form-group :attrs="fields.firstName2" is-sgphx>
                  <text-field v-model="fields.firstName2.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('lastName2') && !isArabicLanguage">
                <form-group :attrs="fields.lastName2" is-sgphx>
                  <text-field v-model="fields.lastName2.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('email')">
                <form-group :attrs="fields.email" is-sgphx>
                  <text-field v-model="fields.email.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('phone')">
                <form-group :attrs="fields.phone" is-sgphx>
                  <div>
                    <phone-field v-model="fields.phone.value" is-sgphx />
                  </div>
                </form-group>
              </li>

              <li>
                <form-group :attrs="fields.preferredContact" is-sgphx>
                  <checkbox-list-field v-model="fields.preferredContact.value" inline is-sgphx />
                </form-group>
              </li>

              <li>
                <form-group :attrs="fields.country" is-sgphx>
                  <select-field v-model="fields.country.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="fieldIsPresent('address9')">
                <form-group :attrs="fields.address9" is-sgphx>
                  <select-field v-model="fields.address9.value" is-sgphx />
                </form-group>
              </li>

              <li>
                <form-group :attrs="fields.additionalInfo" is-sgphx>
                  <textarea-field v-model="fields.additionalInfo.value" is-sgphx />
                </form-group>
              </li>

              <li v-if="shouldDisplayNewsletter">
                <div>
                  <form-group :show-label="false" :attrs="fields.newsletter" is-sgphx>
                    <checkbox-field v-model="fields.newsletter.value" is-sgphx />
                  </form-group>
                </div>
              </li>
            </ul>
          </template>
        </phoenix-form>
      </div>
    </template>
  </panel>
</template>
